// Estado leído/no-leído de notificaciones — solo cliente, no toca backend.
//
// Decisiones:
//  - Persistimos en localStorage un Set<number> de IDs leídos. Como el back
//    solo devuelve las últimas 48h, el set crece muy poco; aún así, en cada
//    refresh purgamos IDs que ya no están en la respuesta del back para que
//    no acumule basura indefinidamente.
//  - Singleton observable: el badge del BottomNav y la propia pestaña Avisos
//    suscriben al mismo estado. Refrescamos al montar la app, al volver
//    visible la pestaña, y cada 2 min en background. Sin WebSocket — en una
//    PWA con visibilidad típica (consultas puntuales) es suficiente.
//
// Limitación conocida: el estado leído/no-leído es POR DISPOSITIVO. Si el
// user lee una notificación en el iPhone, en el PC seguirá apareciendo como
// no-leída. Sincronizar entre dispositivos requiere ReadAt en BD.

import { getMyNotifications } from './api';
import type { MyNotification } from './api';

const READ_IDS_KEY = 'READ_NOTIFICATION_IDS';
const REFRESH_INTERVAL_MS = 2 * 60 * 1000;

function loadReadIds(): Set<number> {
  try {
    const raw = localStorage.getItem(READ_IDS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as number[];
    return new Set(arr.filter((n) => Number.isFinite(n)));
  } catch {
    return new Set();
  }
}

function saveReadIds(ids: Set<number>) {
  try {
    localStorage.setItem(READ_IDS_KEY, JSON.stringify([...ids]));
  } catch { /* quota — ignoramos */ }
}

let readIds: Set<number> = loadReadIds();
let lastList: MyNotification[] = [];
let unreadCount = 0;
let refreshing = false;
let intervalId: ReturnType<typeof setInterval> | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

function emit() { listeners.forEach((l) => l()); }

function recomputeUnread() {
  let n = 0;
  for (const item of lastList) {
    if (!readIds.has(item.idNotificationLog)) n++;
  }
  unreadCount = n;
}

export function getUnreadCount(): number {
  return unreadCount;
}

export function getCachedNotifications(): MyNotification[] {
  return lastList;
}

export function isRead(id: number): boolean {
  return readIds.has(id);
}

export function markAsRead(id: number) {
  if (readIds.has(id)) return;
  readIds.add(id);
  saveReadIds(readIds);
  recomputeUnread();
  // Renovar referencia de lastList para que useSyncExternalStore detecte
  // el cambio y re-renderice las cards (cuya apariencia depende del set
  // de leídos, no de los items en sí).
  lastList = lastList.slice();
  emit();
}

export function markAllAsRead() {
  let changed = false;
  for (const item of lastList) {
    if (!readIds.has(item.idNotificationLog)) {
      readIds.add(item.idNotificationLog);
      changed = true;
    }
  }
  if (!changed) return;
  saveReadIds(readIds);
  recomputeUnread();
  lastList = lastList.slice();
  emit();
}

export async function refreshNotifications(): Promise<void> {
  if (refreshing) return;
  refreshing = true;
  try {
    const res = await getMyNotifications(48);
    lastList = res.data ?? [];

    // Purga IDs leídos que ya no están en la ventana de 48h: cuando el
    // back los recicle no tiene sentido seguir guardándolos.
    const presentIds = new Set(lastList.map((n) => n.idNotificationLog));
    let purged = false;
    for (const id of readIds) {
      if (!presentIds.has(id)) { readIds.delete(id); purged = true; }
    }
    if (purged) saveReadIds(readIds);

    recomputeUnread();
    emit();
  } catch { /* sin conexión: dejamos lastList intacto */ }
  finally { refreshing = false; }
}

export function subscribeNotifications(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Arranca el polling global. Se llama una vez desde App.tsx tras login
// (para no fetchear sin token). Idempotente.
export function startNotificationsPolling() {
  if (intervalId) return;
  refreshNotifications();
  intervalId = setInterval(refreshNotifications, REFRESH_INTERVAL_MS);
  // Refrescar al volver visible la pestaña — captura pushes recibidos
  // mientras la PWA estaba en background.
  document.addEventListener('visibilitychange', onVisibility);
}

export function stopNotificationsPolling() {
  if (intervalId) { clearInterval(intervalId); intervalId = null; }
  document.removeEventListener('visibilitychange', onVisibility);
}

function onVisibility() {
  if (document.visibilityState === 'visible') refreshNotifications();
}
